import { validateDomAction } from './agentShared/domActions';

// Client-side dispatcher for agent dom_action events. Defence in depth: every
// action was validated at the gateway, and is RE-validated here against the
// same shared schema before touching the page.
export interface AgentActionHandlers {
  navigate: (route: string) => void;
  loadDraft: (draftId: string) => void;
  suggestActions: (actions: Array<{ label: string; prompt: string }>) => void;
}

let handlers: Partial<AgentActionHandlers> = {};

export function registerAgentActionHandlers(next: Partial<AgentActionHandlers>): () => void {
  handlers = { ...handlers, ...next };
  return () => {
    for (const key of Object.keys(next) as Array<keyof AgentActionHandlers>) {
      delete handlers[key];
    }
  };
}

const highlight = (target: string): boolean => {
  const el = document.querySelector(`[data-agent-target="${target}"]`);
  if (!el) return false;
  el.classList.remove('agent-highlight');
  // restart the animation if it was already highlighted
  void (el as HTMLElement).offsetWidth;
  el.classList.add('agent-highlight');
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  window.setTimeout(() => el.classList.remove('agent-highlight'), 2600);
  return true;
};

export function dispatchAgentAction(
  action: string,
  params: Record<string, unknown>,
): { applied: boolean; reason?: string } {
  const check = validateDomAction(action, params);
  if (!check.ok) return { applied: false, reason: check.errors.join('; ') };

  switch (action) {
    case 'navigateRoute': {
      if (!handlers.navigate) return { applied: false, reason: 'navigation not available here' };
      handlers.navigate(`/app/${params.route as string}`);
      return { applied: true };
    }
    case 'highlightTarget': {
      const ok = highlight(params.target as string);
      return ok ? { applied: true } : { applied: false, reason: 'target not on screen' };
    }
    case 'loadDraft': {
      if (!handlers.loadDraft) return { applied: false, reason: 'form builder not open' };
      handlers.loadDraft(params.draft_id as string);
      return { applied: true };
    }
    case 'suggestActions': {
      if (!handlers.suggestActions) return { applied: false, reason: 'assistant panel not open' };
      handlers.suggestActions(params.actions as Array<{ label: string; prompt: string }>);
      return { applied: true };
    }
    default:
      return { applied: false, reason: `unhandled action ${action}` };
  }
}
